import React, { useState, useEffect } from "react";
import { Button, Segment, Input, Select, Checkbox } from "semantic-ui-react";
import Role from "../../../models/Role";
import NumberInput from "../../../assets/components/NumberInput";

const Editor = ({ onSave, onClose, editing = null }) => {
    const [roles, setRoles] = useState([]);
    const [role, setRole] = useState(
        editing?.roleKey ?? "MASHIE_COUNTRY_MEMBERSHIP",
    );
    const [key, setKey] = useState(editing?.key ?? "");
    const [name, setName] = useState(editing?.name ?? "");
    const [duration, setDuration] = useState(editing?.duration ?? 365);
    const [cost, setCost] = useState(editing?.cost ?? 0);
    const [eventCredit, setEventCredit] = useState(editing?.eventCredit ?? 0);
    const [teeTimeCredit, setTeeTimeCredit] = useState(
        editing?.teeTimeCredit ?? 0,
    );

    const [useForLinkSignup, setUseForLinkSignup] = useState(
        editing?.useForLinkSignup ?? false,
    );

    const [defaultCommencementMonth, setDefaultCommencementMonth] = useState(
        editing?.defaultCommencementMonth ?? null,
    );

    const [renewalExempt, setRenewalExempt] = useState(
        editing?.renewalExempt ?? true,
    );

    const commencementMonths = [
        { key: 0, value: null, text: "Current month" },
        ...Array.from({ length: 12 }, (_, index) => ({
            key: index + 1,
            value: index + 1,
            text: new Date(2000, index, 1).toLocaleString("default", {
                month: "long",
            }),
        })),
    ];

    const durations = [
        {
            key: 0,
            value: 30,
            text: "One month",
        },
        {
            key: 1,
            value: 90,
            text: "Three months",
        },
        {
            key: 2,
            value: 180,
            text: "Six months",
        },
        {
            key: 3,
            value: 365,
            text: "One year",
        },
    ];

    useEffect(() => {
        const membershipRoleKeys = [
            "MASHIE_COUNTRY_MEMBERSHIP",
            "MASHIE_LINKS_MEMBERSHIP",
            "MASHIE_CLUB_MEMBERSHIP",
            "MASHIE_TRIAL_MEMBERSHIP",
            "MASHIE_FOUNDER_MEMBERSHIP",
        ];
        const getRoles = async () => {
            const { data } = await Role.index();
            setRoles(
                data.filter(({ key }) => membershipRoleKeys.includes(key)),
            );
        };
        getRoles();
    }, []);

    return (
        <div className='mt-3'>
            <Segment vertical>
                <label className='with-input'>Membership key</label>
                <p>
                    <small>
                        This key will be used by the entity in requests to
                        create members to define waht level of membership should
                        be given.
                    </small>
                </p>
                <Input
                    placeholder='Membership key (all caps, replace spaces with underscores).'
                    fluid
                    value={key}
                    onChange={({ target }) => setKey(target.value)}
                />
            </Segment>
            <Segment vertical>
                <label className='with-input'>Membership name</label>
                <p>
                    <small>
                        This is the name the member will see within the app.
                    </small>
                </p>
                <Input
                    placeholder='Membership name (this is what the member will see)'
                    fluid
                    value={name}
                    onChange={({ target }) => setName(target.value)}
                />
            </Segment>
            <Segment vertical>
                <label className='with-input'>Mashie membership</label>
                <p>
                    <small>
                        This is the level of membership the member will receive.
                    </small>
                </p>
                <Select
                    search
                    value={role}
                    onChange={(_, { value }) => setRole(value)}
                    options={roles.map((item, index) => ({
                        key: index,
                        text: item.name,
                        value: item.key,
                    }))}
                />
            </Segment>
            <Segment vertical>
                <label className='with-input'>Duration</label>
                <Select
                    search
                    value={duration}
                    onChange={(_, { value }) => setDuration(value)}
                    options={durations}
                />
            </Segment>
            <Segment vertical>
                <label className='with-input'>Cost</label>
                <NumberInput
                    decimal
                    icon='gbp'
                    iconPosition='left'
                    value={cost}
                    onChange={setCost}
                />
            </Segment>
            <Segment vertical>
                <label className='with-input'>Event credit</label>
                <NumberInput
                    decimal
                    icon='gbp'
                    iconPosition='left'
                    value={eventCredit}
                    onChange={setEventCredit}
                />
            </Segment>
            <Segment vertical>
                <label className='with-input'>Green Fee Credit</label>
                <NumberInput
                    decimal
                    icon='gbp'
                    iconPosition='left'
                    value={teeTimeCredit}
                    onChange={setTeeTimeCredit}
                />
            </Segment>
            <Segment vertical>
                <Checkbox
                    label='Renewal exempt'
                    checked={renewalExempt}
                    onChange={(_, { checked }) => setRenewalExempt(checked)}
                />
                <p>
                    <small>
                        If enabled, this membership will not be included in
                        normal automatic membership renewal creation. Untick
                        this to allow automatic membership renewals.
                    </small>
                </p>
            </Segment>
            <Segment vertical>
                <Checkbox
                    label='Use for membership link signup'
                    checked={useForLinkSignup}
                    onChange={(_, { checked }) => setUseForLinkSignup(checked)}
                />
                <p>
                    <small>
                        If enabled, this alias can be used as the default
                        membership when a customer signs up through a membership
                        link.
                    </small>
                </p>
            </Segment>

            <Segment vertical>
                <label className='with-input'>Default commencement month</label>
                <p>
                    <small>
                        Leave as current month to start memberships from the
                        current month, or choose a fixed month such as May.
                    </small>
                </p>
                <Select
                    search
                    clearable
                    value={defaultCommencementMonth}
                    onChange={(_, { value }) =>
                        setDefaultCommencementMonth(value ?? null)
                    }
                    options={commencementMonths}
                />
            </Segment>
            <Segment vertical>
                <Button onClick={onClose} className='mt-3'>
                    Cancel
                </Button>
                <Button
                    primary
                    onClick={() =>
                        onSave({
                            key,
                            name,
                            role,
                            duration,
                            cost,
                            eventCredit,
                            teeTimeCredit,
                            useForLinkSignup,
                            defaultCommencementMonth,
                            renewalExempt,
                        })
                    }
                    className='theme mt-3'
                >
                    Save
                </Button>
            </Segment>
        </div>
    );
};

export default Editor;
